import { API_ENDPOINTS, getAuthHeaders } from '../config/api';

// ============================================
// Authentication Services
// ============================================

export const authService = {
  // Login
  async login(email, password) {
    try {
      const response = await fetch(API_ENDPOINTS.LOGIN, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Register
  async register(userData) {
    try {
      const response = await fetch(API_ENDPOINTS.REGISTER, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(userData)
      });

      const data = await response.json();

      if (data.success) {
        localStorage.setItem('token', data.data.token);
        localStorage.setItem('user', JSON.stringify(data.data.user));
      }

      return data;
    } catch (error) {
      throw error;
    }
  },

  // Get current user
  async getMe() {
    try {
      const response = await fetch(API_ENDPOINTS.GET_ME, {
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Logout
  logout() {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },

  // Check if user is authenticated
  isAuthenticated() {
    return !!localStorage.getItem('token');
  },

  // Get stored user
  getUser() {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }
};

// ============================================
// Course Services
// ============================================

export const courseService = {
  // Get all courses
  async getCourses(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_ENDPOINTS.COURSES}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      return data.data.courses;
    } catch (error) {
      throw error;
    }
  },

  // Get featured courses
  async getFeaturedCourses() {
    try {
      const response = await fetch(API_ENDPOINTS.FEATURED_COURSES);
      const data = await response.json();
      
      return data.data.courses;
    } catch (error) {
      throw error;
    }
  },

  // Get course by slug
  async getCourseBySlug(slug) {
    try {
      const response = await fetch(API_ENDPOINTS.COURSE_BY_SLUG(slug));
      const data = await response.json();
      
      return data.data.course;
    } catch (error) {
      throw error;
    }
  },

  // Create course (Admin)
  async createCourse(courseData) {
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_COURSE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(courseData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update course (Admin)
  async updateCourse(id, courseData) {
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_COURSE(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(courseData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete course (Admin)
  async deleteCourse(id) {
    try {
      const response = await fetch(API_ENDPOINTS.DELETE_COURSE(id), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// Blog Services
// ============================================

export const blogService = {
  // Get all blogs
  async getBlogs(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_ENDPOINTS.BLOGS}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      const data = await response.json();
      
      return data.data.blogs;
    } catch (error) {
      throw error;
    }
  },

  // Get featured blogs
  async getFeaturedBlogs() {
    try {
      const response = await fetch(API_ENDPOINTS.FEATURED_BLOGS);
      const data = await response.json();
      
      return data.data.blogs;
    } catch (error) {
      throw error;
    }
  },

  // Get blog by slug
  async getBlogBySlug(slug) {
    try {
      const response = await fetch(API_ENDPOINTS.BLOG_BY_SLUG(slug));
      const data = await response.json();
      
      return data.data.blog;
    } catch (error) {
      throw error;
    }
  },

  // Create blog (Admin)
  async createBlog(blogData) {
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_BLOG, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(blogData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update blog (Admin)
  async updateBlog(id, blogData) {
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_BLOG(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(blogData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete blog (Admin)
  async deleteBlog(id) {
    try {
      const response = await fetch(API_ENDPOINTS.DELETE_BLOG(id), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// Testimonial Services
// ============================================

export const testimonialService = {
  // Get all testimonials
  async getTestimonials() {
    try {
      const response = await fetch(API_ENDPOINTS.TESTIMONIALS);
      const data = await response.json();
      
      return data.data.testimonials;
    } catch (error) {
      throw error;
    }
  },

  // Get featured testimonials
  async getFeaturedTestimonials() {
    try {
      const response = await fetch(API_ENDPOINTS.FEATURED_TESTIMONIALS);
      const data = await response.json();
      
      return data.data.testimonials;
    } catch (error) {
      throw error;
    }
  },

  // Create testimonial (Admin)
  async createTestimonial(testimonialData) {
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_TESTIMONIAL, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(testimonialData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update testimonial (Admin)
  async updateTestimonial(id, testimonialData) {
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_TESTIMONIAL(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(testimonialData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete testimonial (Admin)
  async deleteTestimonial(id) {
    try {
      const response = await fetch(API_ENDPOINTS.DELETE_TESTIMONIAL(id), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// Partner Services
// ============================================

export const partnerService = {
  // Public: get active partners
  async getPartners() {
    try {
      const response = await fetch(API_ENDPOINTS.PARTNERS);
      const data = await response.json();
      return data.data.partners || [];
    } catch (error) {
      throw error;
    }
  },

  // Admin: list all
  async getAllPartners() {
    try {
      const response = await fetch(API_ENDPOINTS.PARTNERS_ALL, { headers: getAuthHeaders() });
      const data = await response.json();
      return data.data.partners || [];
    } catch (error) {
      throw error;
    }
  },

  async createPartner(payload) {
    const res = await fetch(API_ENDPOINTS.CREATE_PARTNER, {
      method: 'POST',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return res.json();
  },

  async updatePartner(id, payload) {
    const res = await fetch(API_ENDPOINTS.UPDATE_PARTNER(id), {
      method: 'PUT',
      headers: getAuthHeaders(),
      body: JSON.stringify(payload)
    });
    return res.json();
  },

  async deletePartner(id) {
    const res = await fetch(API_ENDPOINTS.DELETE_PARTNER(id), {
      method: 'DELETE',
      headers: getAuthHeaders()
    });
    return res.json();
  }
};

// ============================================
// Contact Form Services
// ============================================

export const contactService = {
  // Submit contact form
  async submitContact(contactData) {
    try {
      const response = await fetch(API_ENDPOINTS.CONTACT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(contactData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get all contacts (Admin)
  async getContacts(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_ENDPOINTS.CONTACT}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// ============================================
// Enrollment Services
// ============================================

export const enrollmentService = {
  // Submit enrollment
  async submitEnrollment(enrollmentData) {
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_ENROLLMENT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(enrollmentData)
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get all enrollments (Admin)
  async getEnrollments(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_ENDPOINTS.ENROLLMENTS}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url, {
        headers: getAuthHeaders()
      });

      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};

// Hero Slides Service
export const heroSlideService = {
  // Get all hero slides (Public)
  async getAll(params = {}) {
    try {
      const queryString = new URLSearchParams(params).toString();
      const url = `${API_ENDPOINTS.HERO_SLIDES}${queryString ? `?${queryString}` : ''}`;
      
      const response = await fetch(url);
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Get only active/public slides and return normalized array
  async getPublicSlides() {
    try {
      const response = await fetch(API_ENDPOINTS.HERO_SLIDES);
      const data = await response.json();
      if (data && data.success && Array.isArray(data.data)) {
        return data.data;
      }
      return [];
    } catch (error) {
      return [];
    }
  },

  // Get single hero slide
  async getById(id) {
    try {
      const response = await fetch(API_ENDPOINTS.HERO_SLIDE_BY_ID(id));
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Create hero slide (Admin)
  async create(slideData) {
    try {
      const response = await fetch(API_ENDPOINTS.CREATE_HERO_SLIDE, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(slideData)
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update hero slide (Admin)
  async update(id, slideData) {
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_HERO_SLIDE(id), {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify(slideData)
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Delete hero slide (Admin)
  async delete(id) {
    try {
      const response = await fetch(API_ENDPOINTS.DELETE_HERO_SLIDE(id), {
        method: 'DELETE',
        headers: getAuthHeaders()
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  },

  // Update slides order (Admin)
  async updateOrder(slides) {
    try {
      const response = await fetch(API_ENDPOINTS.UPDATE_SLIDES_ORDER, {
        method: 'PUT',
        headers: getAuthHeaders(),
        body: JSON.stringify({ slides })
      });
      return await response.json();
    } catch (error) {
      throw error;
    }
  }
};
